import {
  AspectRatio,
  Divider,
  Image,
  NumberFormatter,
  Progress,
} from '@mantine/core'
import clsx from 'clsx'
import classes from './TeamMatchStat.module.css'
import {
  IconArrowDown,
  IconArrowUp,
  IconPoint,
  IconStar,
} from '@tabler/icons-react'
import { getRankSuffix } from '../../common/helpers'
import { TeamMatchHistory } from '../../matches/api'
import { useTeamStats } from '../../teams/api'
import { useParams } from 'react-router'
import LastFiveComponent from '../LastFive/LastFive'
import { Typography } from '../../common/typography'

type ResultKey = 'team1_wins' | 'draws' | 'team1_losses'

function getWinDrawLossPercentage(
  data: TeamMatchHistory,
  key: ResultKey
): number {
  const total = data.team1_wins + data.draws + data.team1_losses
  if (total === 0) return 0 // avoid division by zero

  const value = data[key]
  return Math.round((value / total) * 100)
}

export default function TeamMatchStat({
  match_history,
}: {
  match_history: TeamMatchHistory
}) {
  const { competitionId } = useParams()
  const { data: team1 } = useTeamStats({
    competition_id: Number(competitionId),
    team_id: match_history.team1_id,
  })
  const { data: team2 } = useTeamStats({
    competition_id: Number(competitionId),
    team_id: match_history.team2_id,
  })
  return (
    <div className={classes.container}>
      <div className={classes.teams_match_stats_container}>
        <AspectRatio ratio={1 / 1}>
          <Image
            src={match_history.team1_logo.thumbnail}
            alt={match_history.team1_name}
            className={classes.team_logo}
            fit="scale-down"
          />
        </AspectRatio>
        <div className={classes.team_stats}>
          <div className={clsx(classes.box, classes.home_team)}>
            <Typography
              fc={{ light: 'white', dark: 'white' }}
              fs="xs"
              ta="center"
              fw={500}
              lh="130"
            >
              Won
            </Typography>
          </div>
          <Typography
            fs="xs"
            fc={{ light: 'black', dark: 'gray-3' }}
            ta="center"
            ff="JetBrains Mono"
            lh="140"
          >
            {match_history.team1_wins}
          </Typography>
        </div>
        <div className={classes.team_stats}>
          <div className={classes.box}>
            <Typography
              fc={{ light: 'white', dark: 'white' }}
              fs="xs"
              ta="center"
              fw={500}
              lh="130"
            >
              Draw
            </Typography>
          </div>
          <Typography
            fs="xs"
            fc={{ light: 'black', dark: 'gray-3' }}
            ta="center"
            ff="JetBrains Mono"
            lh="140"
          >
            {match_history.draws}
          </Typography>
        </div>
        <div className={classes.team_stats}>
          <div className={clsx(classes.box, classes.away_team)}>
            <Typography
              fc={{ light: 'white', dark: 'white' }}
              fs="xs"
              ta="center"
              fw={500}
              lh="130"
            >
              Won
            </Typography>
          </div>
          <Typography
            fs="xs"
            fc={{ light: 'black', dark: 'gray-3' }}
            ta="center"
            ff="JetBrains Mono"
            lh="140"
          >
            {match_history.team1_losses}
          </Typography>
        </div>
        <AspectRatio ratio={1 / 1}>
          <Image
            src={match_history.team2_logo.thumbnail}
            alt={match_history.team2_name}
            className={classes.team_logo}
            fit="scale-down"
          />
        </AspectRatio>
      </div>
      <Progress.Root classNames={{ root: classes.root, label: classes.label }}>
        <Progress.Section
          value={getWinDrawLossPercentage(match_history, 'team1_wins')}
          color="#8073AD"
        >
          <Progress.Label>
            {getWinDrawLossPercentage(match_history, 'team1_wins')}%
          </Progress.Label>
        </Progress.Section>
        <Progress.Section
          value={getWinDrawLossPercentage(match_history, 'draws')}
          color="#495057"
        >
          <Progress.Label>
            {getWinDrawLossPercentage(match_history, 'draws')}%
          </Progress.Label>
        </Progress.Section>
        <Progress.Section
          value={getWinDrawLossPercentage(match_history, 'team1_losses')}
          color="#B2675E"
        >
          <Progress.Label>
            {getWinDrawLossPercentage(match_history, 'team1_losses')}%
          </Progress.Label>
        </Progress.Section>
      </Progress.Root>
      <div className={classes.stat_row}>
        <div className={clsx(classes.stat_box, classes.stat_box_rating)}>
          <IconStar className={classes.stat_icon} />
          <NumberFormatter
            className={classes.stat_value}
            decimalScale={2}
            fixedDecimalScale
            value={match_history.team1_rating}
          />
        </div>

        <Typography
          fs="xs"
          fc={{ light: 'black', dark: 'gray-3' }}
          ta="center"
          fw={500}
          lh="130"
        >
          Rating
        </Typography>

        <div className={clsx(classes.stat_box, classes.stat_box_rating)}>
          <NumberFormatter
            className={classes.stat_value}
            decimalScale={2}
            fixedDecimalScale
            value={match_history.team2_rating}
          />
          <IconStar className={classes.stat_icon} />
        </div>
      </div>
      <Divider />
      <div className={classes.stat_row}>
        <div className={classes.stat_box}>
          {match_history.team1_rank_change &&
          match_history.team1_rank_change > 0 ? (
            <IconArrowUp className={clsx(classes.icon, classes.up)} />
          ) : match_history.team1_rank_change &&
            match_history.team1_rank_change < 0 ? (
            <IconArrowDown className={clsx(classes.icon, classes.down)} />
          ) : (
            <IconPoint className={classes.icon} />
          )}
          {match_history.team1_rank && (
            <Typography
              fs="sm"
              fc={{ light: 'black', dark: 'gray-3' }}
              ta="center"
              lh="140"
            >
              {match_history.team1_rank}
              <sup>{getRankSuffix(match_history.team1_rank)}</sup>
            </Typography>
          )}
        </div>

        <Typography
          fs="xs"
          fc={{ light: 'black', dark: 'gray-3' }}
          ta="center"
          fw={500}
          lh="130"
        >
          Rank
        </Typography>

        <div className={classes.stat_box}>
          {match_history.team2_rank && (
            <Typography
              fs="sm"
              fc={{ light: 'black', dark: 'gray-3' }}
              ta="center"
              lh="140"
            >
              {match_history.team2_rank}
              <sup>{getRankSuffix(match_history.team2_rank)}</sup>
            </Typography>
          )}

          {match_history.team2_rank_change &&
          match_history.team2_rank_change > 0 ? (
            <IconArrowUp className={clsx(classes.icon, classes.up)} />
          ) : match_history.team2_rank_change &&
            match_history.team2_rank_change < 0 ? (
            <IconArrowDown className={clsx(classes.icon, classes.down)} />
          ) : (
            <IconPoint className={classes.icon} />
          )}
        </div>
      </div>
      <Divider />
      <div className={classes.stat_row}>
        <div className={classes.last_five_container}>
          <LastFiveComponent last_five={team1.last_5_matches} />
        </div>
        <Typography
          fs="xs"
          fc={{ light: 'black', dark: 'gray-3' }}
          ta="center"
          fw={500}
          lh="130"
        >
          Form
        </Typography>

        <div
          className={clsx(
            classes.last_five_container,
            classes.last_five_container_away_team
          )}
        >
          <LastFiveComponent last_five={team2.last_5_matches} />
        </div>
      </div>
    </div>
  )
}
